import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Button,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import TableCompo from "../CommonTable/TableCompo";
import { getSipPlansRequest } from "../../redux/Sip/sipSlice";
import { useAppTheme } from "../../theme/useAppTheme";
import useCurrency from "../../hooks/useCurrency";

const CustomerSipScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { sipPlans, loading, error } = useSelector((state) => state.sip || {});

  useEffect(() => {
    dispatch(getSipPlansRequest());
  }, [dispatch]);

  const themeColors = useAppTheme();
  const { symbol, code } = useCurrency();

  const formatDateTime = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp);
    return date.toLocaleString("en-IN", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const calculateEndDate = (startDate, months) => {
    if (!startDate || !months) return "N/A";
    const end = new Date(startDate);
    end.setMonth(end.getMonth() + months);
    return end.toLocaleString("en-IN", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatCurrency = (value) => {
    const n = Number(value);
    if (isNaN(n)) return `${symbol}0`;
    return value.toLocaleString("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatWeight = (value) => {
    if (isNaN(value)) return "0 g";
    return `${value.toLocaleString("en-IN", {
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    })} g`;
  };

  const handleSipRowClick = (sipItem) => {
    if (!sipItem) {
      console.log("sipItem is NULL — cannot navigate");
      return;
    }

    navigate("/installmentScreen", {
      state: {
        sipData: sipItem,
      },
    });
  };

  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);

  const columns = [
    { field: "createdAt", headerName: "Created On", flex: 1.5 },
    { field: "endsOn", headerName: "Ends On", flex: 1.5 },
    { field: "metal", headerName: "Metal", flex: 1 },
    { field: "planName", headerName: "Plan Name", flex: 1.5 },
    { field: "installments", headerName: "Total Installments", flex: 1 },
    { field: "paidInstallments", headerName: "Paid Installments", flex: 1 },
    { field: "totalPaidAmount", headerName: "Total Paid Amount", flex: 1.2 },
    { field: "totalWeight", headerName: "Total Weight", flex: 1.2 },
    { field: "installmentAmount", headerName: "Installment Amount", flex: 1 },
    { field: "durationType", headerName: "Duration Type", flex: 1 },
    { field: "status", headerName: "Status", flex: 1 },
    { field: "tag", headerName: "Tag", flex: 1 },
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      render: (value, row) => (
        <Button
          variant="contained"
          sx={{
            background: themeColors.mediumContainer,
            color: themeColors.text,
            border: `1px solid ${themeColors.scaffold}`,
            textTransform: "none",
            fontWeight: 600,
            "&:hover": { background: themeColors.svgColor },
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleSipRowClick(row?.original ?? null);
          }}
        >
          Pay
        </Button>
      ),
    },
  ];

  const tableData = Array.isArray(sipPlans)
    ? sipPlans.map((item, index) => {
        const paidInstallments = Array.isArray(item?.sipInstallments)
          ? item.sipInstallments.filter((i) => i.status === "PAID")
          : [];

        const paidCount = paidInstallments.length;
        const totalPaidAmount = paidInstallments.reduce(
          (sum, i) => sum + (i.totalPaidAmount || 0),
          0,
        );
        const totalWeight = paidInstallments.reduce(
          (sum, i) => sum + (i.weight || 0),
          0,
        );

        return {
          id: index + 1,
          createdAt: formatDateTime(item?.createdAt),
          endsOn: calculateEndDate(item?.startDate, item?.noOfMonths),
          metal: item?.bullion?._id
            ? `${item?.bullion?.metalName} ${item?.bullion?.karat}K-${item?.bullion?.purity}`
            : "Money",
          planName: item?.sipplan?.name || "N/A",
          installments: item?.sipplan?.maxDuration || 0,
          paidInstallments: paidCount,
          totalPaidAmount: formatCurrency(totalPaidAmount),
          totalWeight: formatWeight(totalWeight),
          installmentAmount: item?.installmentAmount
            ? formatCurrency(item?.installmentAmount)
            : "N/A",
          status: item?.status || "N/A",
          durationType: item?.sipplan?.durationType,
          tag: item?.tag,
          action: "start",
          original: item,
        };
      })
    : [];

  const paginatedData = tableData.slice(page * limit, page * limit + limit);

  const handleTableChange = (type, payload) => {
    if (type === "changePage") {
      setPage(payload.page);
    }

    if (type === "changeRowsPerPage") {
      setLimit(payload.rowsPerPage);
      setPage(0);
    }
  };

  return (
    <Box
      sx={{ width: "100%", display: "flex", justifyContent: "center", mt: 8 }}
    >
      <Card
        sx={{
          width: "98%",
          maxWidth: "1300px",
          boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
          borderRadius: 3,
          background: themeColors.scaffoldGradientColors,
        }}
      >
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: themeColors.primary,
                fontFamily: "Aeonik",
              }}
            >
              SIP Plans
            </Typography>

            <Button
              variant="contained"
              sx={{
                background: themeColors.mediumContainer,
                color: themeColors.text,
                border: `1px solid ${themeColors.scaffold}`,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "15px",
                px: 3,
                "&:hover": { background: themeColors.svgColor },
              }}
              onClick={() => navigate("/AllSipScreen")}
            >
              Start New SIP
            </Button>
          </Box>

          {loading ? (
            <Box sx={{ textAlign: "center", mt: 10 }}>
              <CircularProgress color="primary" />
              <Typography variant="body2" mt={2}>
                Loading SIP plans...
              </Typography>
            </Box>
          ) : error ? (
            <Typography color="error" textAlign="center" mt={5}>
              {error}
            </Typography>
          ) : (
            <Box sx={{ height: "70vh", width: "100%", minWidth: 0, overflow: "auto" }}>
              <TableCompo
                title="SIP Plan History"
                columns={columns}
                data={paginatedData}
                page={page}
                limit={limit}
                totalCount={tableData.length}
                onTableChange={handleTableChange}
                loading={loading}
              />
            </Box>
          )}
        </CardContent>
      </Card>

    </Box>
  );
};

export default CustomerSipScreen;
